import { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, StatusPill } from "../../components/Bits";
import { api } from "../../lib/api";
import { Users, Receipt, Wrench, CalendarRange, DoorOpen, Banknote, BedDouble, AlertTriangle } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "Active residents", value: stats.total_students, icon: Users, tint: "bg-[#DCE3D6] text-[#4A5D3E]" },
        { label: "Beds available", value: `${stats.free_beds}/${stats.total_beds}`, icon: BedDouble, tint: "bg-[#DCE3D6] text-[#4A5D3E]" },
        { label: "Fees collected", value: `₹ ${stats.fees_collected.toLocaleString()}`, icon: Banknote, tint: "bg-[#E8C2B8] text-[#8C3F2C]" },
        { label: "Fees pending", value: `₹ ${stats.fees_pending.toLocaleString()}`, icon: Receipt, tint: "bg-[#FBEED7] text-[#8C6D31]" },
        { label: "Pending fines", value: `₹ ${(stats.pending_fines || 0).toLocaleString()}`, icon: AlertTriangle, tint: "bg-[#F5D8D8] text-[#8C3F3F]" },
        { label: "Open tickets", value: stats.open_tickets, icon: Wrench, tint: "bg-[#D6E6EB] text-[#3D5C68]" },
        { label: "Pending leaves", value: stats.pending_leaves, icon: CalendarRange, tint: "bg-[#F4F1EA] text-[#75716E]" },
        { label: "Visitors today", value: stats.visitors_today, icon: DoorOpen, tint: "bg-[#EAE5DE] text-[#5A5652]" },
      ]
    : [];

  return (
    <AppShell>
      <PageHeader
        overline="Admin overview"
        title="Good to see you again."
        subtitle="A calm summary of what's happening inside Cozy Heaven today."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="stat-grid">
        {stats === null
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-28 rounded-lg border border-warm bg-white animate-pulse" />
            ))
          : cards.map((c) => (
              <div key={c.label} className="bg-white border border-warm rounded-lg p-5 card-lift" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className={`inline-flex items-center justify-center h-9 w-9 rounded-md ${c.tint}`}>
                  <c.icon className="h-4 w-4" />
                </div>
                <div className="mt-4 font-body font-semibold text-3xl text-deep tracking-tight">{c.value}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-warm mt-1">{c.label}</div>
              </div>
            ))}
      </div>

      <Panel title="Recent notices">
        {stats?.recent_notices?.length ? (
          <ul className="divide-y divide-[#EAE5DE]">
            {stats.recent_notices.map((n) => (
              <li key={n.id} className="py-3 flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-deep">{n.title}</div>
                  <div className="text-sm text-warm line-clamp-1">{n.content}</div>
                </div>
                <StatusPill status={n.priority === "urgent" || n.priority === "high" ? "pending" : "approved"} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-warm">No notices yet. Post one from the Notices section.</div>
        )}
      </Panel>
    </AppShell>
  );
}
